import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Alert,
  AlertTitle,
  LinearProgress,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ApiResponse, ProcessingStatus } from '../types/pipeline';

interface StageProcessorProps<T> {
  title: string;
  description?: string;
  processFunction: () => Promise<ApiResponse<T>>;
  onSuccess: (data: T) => void;
  onError?: (error: string) => void;
  trigger?: any; // Value that triggers reprocessing when changed
  autoProcess?: boolean;
  showStatus?: boolean;
  children?: (data: T | null, status: ProcessingStatus) => React.ReactNode;
}

export function StageProcessor<T>({
  title,
  description,
  processFunction,
  onSuccess,
  onError,
  trigger,
  autoProcess = false,
  showStatus = true,
  children,
}: StageProcessorProps<T>) {
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    message: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(true);
  const isProcessingRef = useRef(false);
  const hasProcessedRef = useRef(false);

  const process = async () => {
    // Prevent duplicate calls
    if (isProcessingRef.current) {
      console.log('Skipping duplicate process call');
      return;
    }
    
    isProcessingRef.current = true;
    
    setStatus({
      isProcessing: true,
      message: `Processing ${title}...`,
      progress: 0,
    });
    setError(null);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setStatus(prev => ({
          ...prev,
          progress: Math.min((prev.progress || 0) + 10, 90),
        }));
      }, 200);

      const response = await processFunction();
      
      clearInterval(progressInterval);

      if (response.success && response.data) {
        setData(response.data);
        onSuccess(response.data);
        setStatus({
          isProcessing: false,
          message: `${title} completed successfully`,
          progress: 100,
        });
      } else {
        throw new Error(response.error || 'Processing failed');
      }
    } catch (err: any) {
      setError(err.message);
      setStatus({
        isProcessing: false,
        message: `Failed to process ${title}`,
        progress: 0,
      });
      if (onError) {
        onError(err.message);
      }
    } finally {
      isProcessingRef.current = false;
      hasProcessedRef.current = true;
    }
  };

  // Auto-process on mount or trigger change
  useEffect(() => {
    if (autoProcess && !data && !isProcessingRef.current) {
      process();
    }
  }, []);

  useEffect(() => {
    if (trigger === undefined || trigger === null) {
      return;
    }

    if (!isProcessingRef.current) {
      process();
    }
  }, [trigger]);

  const renderStatus = () => {
    if (!showStatus) return null;

    if (status.isProcessing) {
      return (
        <Alert severity="info" icon={<LinearProgress />}>
          <AlertTitle>{status.message}</AlertTitle>
          {status.progress !== undefined && (
            <LinearProgress
              variant="determinate"
              value={status.progress}
              sx={{ mt: 1 }}
            />
          )}
        </Alert>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={process}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      );
    }

    if (data && !status.isProcessing) {
      return (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
          {status.message}
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box>
      {(title || description) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              {title && <h3 style={{ margin: 0 }}>{title}</h3>}
              {description && (
                <Box sx={{ mt: 1, color: 'text.secondary' }}>{description}</Box>
              )}
            </Box>
            <Box display="flex" gap={1}>
              {!autoProcess && !status.isProcessing && (
                <Button
                  variant="contained"
                  onClick={process}
                  startIcon={<RefreshIcon />}
                >
                  Process
                </Button>
              )}
              <IconButton onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}

      <Collapse in={showDetails}>
        <Box>
          {renderStatus()}
          {children && (
            <Box sx={{ mt: 2 }}>
              {children(data, status)}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// Loading overlay component for use within stages
export function LoadingOverlay({ 
  isLoading, 
  message = 'Processing...' 
}: { 
  isLoading: boolean; 
  message?: string;
}) {
  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <LinearProgress sx={{ width: '50%', mb: 2 }} />
      <Box>{message}</Box>
    </Box>
  );
}
